import {useRef} from 'react';

import aiAssistantAuraBlobs, {AURA_BLOB_CLASS, AURA_CANVAS} from '../consts/aiAssistant/aiAssistantAuraBlobs';
import aiAssistantAuraConfig from '../consts/aiAssistant/aiAssistantAuraConfig';
import aiAssistantAuraMotionService from '../services/aiAssistant/aiAssistantAuraMotionService';
import aiAssistantVoiceLevelService from '../services/aiAssistant/aiAssistantVoiceLevelService';
import useAnimationLoop from './useAnimationLoop';

const {BASE_CYCLE_S} = aiAssistantAuraConfig;

// % of the blob's box, so the motion scales with the aura
const toPercent = (units, size) => ((units / size) * 100).toFixed(3);

// Writes each blob's transform and opacity every frame, straight to the DOM
const useAiAssistantAura = () => {
    const auraRef = useRef(null);

    useAnimationLoop(() => {
        const field = auraRef.current;
        const blobElements = field.querySelectorAll(`.${AURA_BLOB_CLASS}`);

        // Independent channels, so nothing wanders in sync
        const wander = aiAssistantAuraBlobs.map(() => ({
            x: aiAssistantAuraMotionService.createWanderChannel(),
            y: aiAssistantAuraMotionService.createWanderChannel(),
        }));
        const wanderPace = aiAssistantAuraMotionService.createWanderChannel();

        let phase = 0;
        let elapsedS = 0;

        // Rest pose; the only one under prefers-reduced-motion
        field.style.transform = `scaleY(${aiAssistantAuraMotionService.getFieldScaleY(0)})`;

        return (frameS) => {
            const level = aiAssistantVoiceLevelService.getLevel();

            elapsedS += frameS;
            // Integrated, so a pace change continues from the current position
            phase =
                (phase + (aiAssistantAuraMotionService.getPace(level, wanderPace(elapsedS)) * frameS) / BASE_CYCLE_S) %
                1;

            field.style.transform = `scaleY(${aiAssistantAuraMotionService.getFieldScaleY(level)})`;

            aiAssistantAuraBlobs.forEach((blob, index) => {
                const {x, y, scale, opacity} = aiAssistantAuraMotionService.getBlobFrame(blob, {
                    level,
                    phase,
                    wanderX: wander[index].x(elapsedS),
                    wanderY: wander[index].y(elapsedS),
                });

                const translateX = toPercent(x, AURA_CANVAS.width);
                const translateY = toPercent(y, AURA_CANVAS.height);
                const blobScale = scale.toFixed(4);

                blobElements[index].style.transform = `translate(${translateX}%, ${translateY}%) scale(${blobScale})`;
                blobElements[index].style.opacity = opacity.toFixed(3);
            });
        };
    });

    return auraRef;
};

export default useAiAssistantAura;
